import {
    ChangeDetectorRef,
    DestroyRef,
    ElementRef,
    QueryList
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { auditTime, startWith } from 'rxjs/operators';
import { animationFrameScheduler } from 'rxjs';

export function initFlyoutObserver(
    sectionWrappers: QueryList<ElementRef>,
    onUpdate: () => void,
    destroyRef: DestroyRef
): IntersectionObserver {
    const observer = new IntersectionObserver(() => onUpdate(), {
        threshold: [0, 0.1, 0.5, 1]
    });

    fromEvent(document, 'scroll', { capture: true } as AddEventListenerOptions)
        .pipe(
            auditTime(0, animationFrameScheduler),
            takeUntilDestroyed(destroyRef)
        )
        .subscribe(() => onUpdate());

    sectionWrappers.changes
        .pipe(startWith(sectionWrappers), takeUntilDestroyed(destroyRef))
        .subscribe((list: QueryList<ElementRef>) => {
            list.forEach((ref) => observer.observe(ref.nativeElement));
            onUpdate();
        });

    return observer;
}

export function computeFlyoutState(sectionWrappers: QueryList<ElementRef>): {
    activeFlyoutIndex: number;
    flyoutFlipUp: boolean;
} {
    const TARGET_Y = 250;
    let bestIdx = -1;
    let bestDist = Infinity;

    const scrollEl = document.querySelector(
        'mat-sidenav-content'
    ) as HTMLElement | null;
    const atBottom = scrollEl
        ? scrollEl.scrollTop + scrollEl.clientHeight >=
          scrollEl.scrollHeight - 50
        : false;

    sectionWrappers.forEach((ref, i) => {
        const subTitle = ref.nativeElement.querySelector(
            '.sub-title'
        ) as HTMLElement | null;
        if (!subTitle) return;
        const rect = subTitle.getBoundingClientRect();
        if (rect.top < -30 || rect.top > window.innerHeight) return;
        if (atBottom) {
            bestIdx = i;
        } else {
            const dist = Math.abs(rect.top - TARGET_Y);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }
    });

    let flyoutFlipUp = false;
    if (bestIdx >= 0) {
        const wrapper = sectionWrappers.get(bestIdx);
        if (wrapper) {
            const subTitle = wrapper.nativeElement.querySelector(
                '.sub-title'
            ) as HTMLElement | null;
            const titleTop = subTitle
                ? subTitle.getBoundingClientRect().top
                : wrapper.nativeElement.getBoundingClientRect().top;
            const footerEl = document.querySelector(
                'app-footer'
            ) as HTMLElement | null;
            const footerTop = footerEl
                ? Math.min(
                      footerEl.getBoundingClientRect().top,
                      window.innerHeight
                  )
                : window.innerHeight;
            flyoutFlipUp = footerTop - titleTop < 360;
        }
    }

    return { activeFlyoutIndex: bestIdx, flyoutFlipUp };
}

/** Breathing room kept between the flyout and the header/footer it must not touch. */
const FLYOUT_EDGE_GAP = 10;

/** Matches the stylesheet's `.sub-title-tooltip { max-height: 520px }`. */
const FLYOUT_MAX_HEIGHT = 520;

/** Matches the stylesheet's `.sub-title-tooltip { top: 15px }` resting offset. */
const FLYOUT_DEFAULT_OFFSET = 15;

/**
 * Keeps the help flyout inside the space between the header and the footer.
 *
 * Runs for the default downward placement as well as the flipped-up one.  It used to return
 * early unless `flyoutFlipUp` was set, which left the common case with nothing but the
 * stylesheet's 520px max-height — so a flyout opening beside a section near the end of the form
 * ran past the footer and off the bottom of the page.  computeFlyoutState only flips up when
 * there is less than 360px of room, so between 360px and 520px the panel neither flipped nor got
 * clamped.
 *
 * Height is capped first and then measured, rather than derived from scrollHeight: the panel is a
 * flex column whose content scrolls internally, so its own scrollHeight collapses to whatever it
 * was last constrained to and the panel could never grow back when space reappeared.
 */
export function positionFlyoutTooltip(
    activeFlyoutIndex: number,
    flyoutFlipUp: boolean,
    sectionWrappers: QueryList<ElementRef>
): void {
    if (activeFlyoutIndex < 0) return;
    const wrapper = sectionWrappers.get(activeFlyoutIndex);
    if (!wrapper) return;
    const subTitle = wrapper.nativeElement.querySelector(
        '.sub-title'
    ) as HTMLElement | null;
    if (!subTitle) return;
    const tooltip = subTitle.querySelector(
        '.sub-title-tooltip'
    ) as HTMLElement | null;
    if (!tooltip) return;

    const subTitleRect = subTitle.getBoundingClientRect();

    const headerEl = document.querySelector('app-header') as HTMLElement | null;
    const headerBottom = headerEl
        ? Math.max(headerEl.getBoundingClientRect().bottom, 0)
        : 0;
    const footerEl = document.querySelector('app-footer') as HTMLElement | null;
    const footerTop = footerEl
        ? Math.min(footerEl.getBoundingClientRect().top, window.innerHeight)
        : window.innerHeight;

    const availableSpace = footerTop - headerBottom - 2 * FLYOUT_EDGE_GAP;
    const maxHeight = Math.min(availableSpace, FLYOUT_MAX_HEIGHT);

    tooltip.style.setProperty('max-height', `${maxHeight}px`, 'important');

    // Measured after the cap is applied, so this is the height the panel actually renders at —
    // shorter than maxHeight whenever the content is short.
    const height = tooltip.getBoundingClientRect().height;

    const idealTop = flyoutFlipUp
        ? subTitleRect.top - FLYOUT_EDGE_GAP - height
        : subTitleRect.top + FLYOUT_DEFAULT_OFFSET;

    // height <= maxHeight <= availableSpace, so the lower bound can never exceed the upper one.
    const topViewport = Math.min(
        Math.max(idealTop, headerBottom + FLYOUT_EDGE_GAP),
        footerTop - FLYOUT_EDGE_GAP - height
    );

    tooltip.style.setProperty(
        'top',
        `${topViewport - subTitleRect.top}px`,
        'important'
    );
}

export function applyFlyoutUpdate(
    sectionWrappers: QueryList<ElementRef>,
    cdr: ChangeDetectorRef,
    positionCallback: () => void,
    applyState: (state: ReturnType<typeof computeFlyoutState>) => void
): void {
    const result = computeFlyoutState(sectionWrappers);
    applyState(result);
    cdr.detectChanges();
    requestAnimationFrame(positionCallback);
}
